// @ts-strict-ignore
import { useFloating, arrow, offset, shift } from "@floating-ui/react-dom";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $isAtNodeEnd } from "@lexical/selection";
import { mergeRegister } from "@lexical/utils";
import { $getSelection, $isRangeSelection } from "lexical";
import { useRef, useEffect, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@aviary/components/Button";
import { l } from "@aviary/locales/i18n";

import * as styles from "./LinkInput.styles";
interface Props {
  isLinkSelected: boolean;
}

const LinkInput = ({ isLinkSelected }: Props) => {
  const { t } = useTranslation();

  const [editor] = useLexicalComposerContext();

  const arrowRef = useRef(null);

  const [isOpen, setIsOpen] = useState<boolean>(isLinkSelected);
  const {
    floating,
    reference,
    x,
    y,
    middlewareData: { arrow: { x: arrowX, y: arrowY } = {} },
  } = useFloating({
    // eslint-disable-next-line react-compiler/react-compiler
    middleware: [arrow({ element: arrowRef }), offset(10), shift()],
  });
  const linkRef = useRef(null);

  const [linkUrl, setLinkUrl] = useState("");

  const positionTooltip = useCallback(() => {
    reference(getBoundingLinkRect());
  }, [reference]);

  // ensure input displays the correct URL
  const updateLinkInput = useCallback(() => {
    const selection = $getSelection();

    positionTooltip();

    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const parent = node?.getParent();
      if ($isLinkNode(parent)) {
        setLinkUrl(parent.getURL());
      } else if ($isLinkNode(node)) {
        setLinkUrl(node.getURL());
      } else {
        setLinkUrl("");
      }
    }
  }, [positionTooltip]);

  const getBoundingLinkRect = () => {
    const currentSelection = window.getSelection();

    if (!linkRef.current && currentSelection.rangeCount > 0) {
      const virtualElement = currentSelection.getRangeAt(0);

      return virtualElement;
    }
  };

  useEffect(() => {
    setIsOpen(isLinkSelected);

    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateLinkInput();
        });
      })
    );
  }, [editor, updateLinkInput, isLinkSelected]);

  // allows for cursor to bookend link
  useEffect(() => {
    editor.getEditorState().read(() => {
      updateLinkInput();
    });
  }, [editor, updateLinkInput]);

  const getSelectedNode = selection => {
    const anchor = selection?.anchor;
    const focus = selection?.focus;
    const anchorNode = anchor?.getNode();
    const focusNode = focus?.getNode();
    if (anchorNode === focusNode) {
      return anchorNode;
    }
    const isBackward = selection.isBackward();

    // selection with mouse from right to left
    if (isBackward) {
      return $isAtNodeEnd(focus) ? anchorNode : focusNode;
    } else {
      return $isAtNodeEnd(anchor) ? focusNode : anchorNode;
    }
  };

  if (!isOpen) {
    return null;
  }

  const handleApplyButtonClicked = () => {
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
      url: linkUrl,
      target: "_blank",
      rel: "noopener noreferrer",
    });
    setIsOpen(false);
  };

  return (
    <div
      css={styles.popperWrapper}
      ref={floating}
      style={{
        top: y ?? 0,
        left: x ?? 0,
      }}
    >
      <div
        css={styles.arrow}
        ref={arrowRef}
        style={{
          top: arrowY ?? 0,
          left: arrowX ?? 0,
        }}
      />

      <div css={styles.linkWrapper}>
        <input
          aria-label={t(l.aviary.richTextEditor.highlightedLink)}
          id="link"
          type="text"
          value={linkUrl}
          onChange={e => {
            setLinkUrl(e.target.value);
          }}
          onBlur={handleApplyButtonClicked}
          css={styles.input}
        />
        <Button isColor="system" onClick={handleApplyButtonClicked}>
          {t(l.aviary.richTextEditor.apply)}
        </Button>
      </div>
    </div>
  );
};

export { LinkInput };
